"use client"

import "./globals.css"
import { useEffect } from "react"

/**
 * Global Error Boundary - catches unhandled errors at the root level.
 *
 * Auto-recovers by reloading once (capped at 2 attempts via sessionStorage
 * to prevent infinite reload loops). Works for any unhandled error —
 * note that RSC header parse errors (E10) are handled by Next.js MPA
 * fallback before this boundary is reached.
 */
export default function GlobalError({
    error,
    reset
}: {
    error: Error & { digest?: string }
    reset: () => void
}) {
    useEffect(() => {
        console.error("[GlobalError]", error.message, error.digest)

        // Auto-reload with retry cap to prevent infinite loops.
        // sessionStorage persists across reloads but clears on tab close.
        const key = "global-error-reload-count"
        const count = parseInt(sessionStorage.getItem(key) || "0", 10)

        if (count < 2) {
            sessionStorage.setItem(key, String(count + 1))
            const timer = setTimeout(() => {
                window.location.reload()
            }, 1500)
            return () => clearTimeout(timer)
        }
        // After 2 failed reloads, stop and let the user decide
    }, [error])

    return (
        <html lang="en">
            <body className="flex min-h-screen items-center justify-center bg-white antialiased">
                <div className="mx-auto max-w-md px-6 py-16 text-center">
                    <div className="mb-6 flex justify-center">
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
                            <svg className="h-7 w-7 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182" />
                            </svg>
                        </div>
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900">Something went wrong</h2>
                    <p className="mt-2 text-[15px] text-gray-500">
                        An unexpected error occurred. Try reloading.
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-6 rounded-xl bg-gray-900 px-5 py-2.5 text-[14px] font-medium text-white transition-colors hover:bg-gray-800"
                    >
                        Reload now
                    </button>
                </div>
            </body>
        </html>
    )
}
